import React from 'react';
import styled from 'styled-components';
import { Steps } from 'antd';
import StyledContainer from '@design-system/styled/Container';
import { Trans } from 'react-i18next';
import useScreenSize from '@source/hooks/useScreenSize';
import { buildI18KeyResponsive, getResponsiveContent } from '@source/hooks/useResponsiveContent';

interface StepsToSellCarProps {
  content: {
    title: string;
    steps: Array<{
      title: string;
      desc: string;
    }>;
  };
}
const StepsToSellCar: React.FC<StepsToSellCarProps> = ({ content }) => {
  const { title, steps } = content;
  const breakpoint = useScreenSize();

  const tTitle = getResponsiveContent(buildI18KeyResponsive(title, ['xs', 'md', 'lg']), breakpoint);
  const tSteps = steps.map((step) => ({
    title: getResponsiveContent(buildI18KeyResponsive(step.title, ['xs', 'md', 'lg']), breakpoint),
    desc: getResponsiveContent(buildI18KeyResponsive(step.desc, ['xs', 'md', 'lg']), breakpoint),
  }));

  return (
    <StepsToSellCarWrapper>
      <Header>
        <Trans i18nKey={tTitle} components={{ span: <span /> }} />
      </Header>
      <StepsWrapper>
        <Steps direction="vertical" current={-1}>
          {tSteps.map((step, index: number) => (
            <Steps.Step
              key={index + 1}
              title={
                <StepTitle>
                  <Trans i18nKey={step.title} />
                </StepTitle>
              }
              description={
                <StepDesc>
                  <Trans i18nKey={step.desc} />
                </StepDesc>
              }
              icon={<StepNumberCircle>{index + 1}</StepNumberCircle>}
            />
          ))}
        </Steps>
      </StepsWrapper>
    </StepsToSellCarWrapper>
  );
};

const StepsToSellCarWrapper = styled(StyledContainer)`
  margin-top: 35px;
  margin-bottom: 45px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 35px;

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    margin: 48px 23px;
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.xl}) {
    margin: 48px auto;
  }
`;

const Header = styled.div`
  ${(props) => props.theme.typo.familyGoogle.medium};
  font-size: 28px;

  line-height: 1em;
  text-align: center;

  .txt--orange {
    ${(props) => props.theme.typo.familyGoogle.bold};
    color: #ff4c00;
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    ${(props) => props.theme.typo.familyGoogle.medium};
    font-size: 33px;
    line-height: 32px;
  }
`;

const StepsWrapper = styled.div`
  max-width: 792px;
  .ant-steps-item {
    &:last-child {
      .ant-steps-item-description {
        padding-bottom: 0;
      }
    }
  }
  .ant-steps-item-content {
    display: flex !important;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;

    .ant-steps-item-title {
      padding-right: 0;
    }

    .ant-steps-item-description {
      padding-bottom: 28px;

      @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
        padding-bottom: 55px;
      }
    }

    @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
      flex-direction: row;
      gap: 30px;
    }
  }

  .ant-steps-vertical .ant-steps-item-tail::after {
    background: #ff4c00 !important;
  }

  .ant-steps-item-icon {
    margin-right: 12px !important;

    .ant-steps-icon {
      display: flex;
      justify-content: center;
    }
  }
`;

const StepNumberCircle = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #ff4c00;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => props.theme.typo.familyGoogle.regular};
  font-size: 14px;
  line-height: 24px;

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    width: 29px;
    height: 29px;
    font-size: 22px;
  }
`;

const StepTitle = styled.div`
  ${(props) => props.theme.typo.familyGoogle.bold};
  font-size: 15px;
  line-height: 16px;
  color: #ff4c00;

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    width: 185px;
    ${(props) => props.theme.typo.familyGoogle.bold};
    font-size: 18px;
    line-height: 22px;
  }
`;

const StepDesc = styled.div`
  ${(props) => props.theme.typo.familyGoogle.medium};
  font-size: 16px;
  line-height: 20px;
  color: #3b3b3b;

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    ${(props) => props.theme.typo.familyGoogle.semiBold};
  }
`;

export default StepsToSellCar;
